import React, { useState, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, ChevronRight, ArrowLeft, Eye, EyeOff } from "lucide-react";
import { useLang, LanguageSwitcher } from "@/lib/lang";
import { Link } from "react-router-dom";

export default function Login() {
  const { t } = useLang();
  const { login, user, ready } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [site, setSite] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/public/site").then((r) => setSite(r.data)).catch(() => {});
  }, []);

  if (ready && user) {
    if (user.role === "driver") return <Navigate to="/driver" replace />;
    if (user.role === "partner") return <Navigate to="/partner-portal" replace />;
    if (user.role === "fleet_owner") return <Navigate to="/fleet-portal" replace />;
    if (user.role === "vehicle_owner") return <Navigate to="/owner-portal" replace />;
    if (user.role === "client") return <Navigate to="/app" replace />;
    if (user.role === "admin") return <Navigate to="/admin" replace />;
    return <Navigate to="/admin" replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const u = await login(email.trim(), password);
      toast.success(`${t("toast_welcome_prefix")} ${u.name}`);
      if (u.role === "driver") navigate("/driver");
      else if (u.role === "partner") navigate("/partner-portal");
      else if (u.role === "fleet_owner") navigate("/fleet-portal");
      else if (u.role === "vehicle_owner") navigate("/owner-portal");
      else if (u.role === "client") navigate("/app");
      else navigate("/admin");
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex">
      {/* Left hero */}
      <div className="hidden lg:flex flex-1 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1608208291890-dcaf52dc98e1?w=1600"
          alt="Lisbon"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/20 to-black/70"></div>
        <div className="relative z-10 flex flex-col justify-between p-16 w-full">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 border border-[#f59e0b] flex items-center justify-center bg-white/10 backdrop-blur">
              <span className="font-display text-[#f59e0b] text-xl">D</span>
            </div>
            <span className="font-display text-2xl tracking-wide text-white">DiTours</span>
          </div>
          <div className="max-w-md">
            <p className="text-[10px] uppercase tracking-widest text-[#f59e0b] font-semibold mb-4">{t("login_since_lisboa")}</p>
            <h1 className="font-display text-5xl font-medium leading-tight text-white mb-6">
              {t("login_hero_h1")}
            </h1>
            <p className="text-slate-200 text-base leading-relaxed">
              {t("login_hero_body")}
            </p>
          </div>
          <div className="text-xs text-slate-300 flex flex-wrap gap-x-6 gap-y-1" data-testid="login-site-info">
            <span>{t("login_licenses")}</span>
            <span>·</span>
            <span>{t("login_powered_by")}</span>
            {site?.support_phone && <><span className="hidden md:inline">·</span><span data-testid="login-site-phone">{site.support_phone}</span></>}
            {site?.admin_email && <><span className="hidden md:inline">·</span><a href={`mailto:${site.admin_email}`} className="hover:text-[#f59e0b]" data-testid="login-site-email">{site.admin_email}</a></>}
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="flex justify-between items-center mb-4">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-[#f59e0b] uppercase tracking-widest font-semibold transition-colors"
              data-testid="login-back-to-site"
            >
              <ArrowLeft className="w-3 h-3" /> Voltar ao site
            </Link>
            <LanguageSwitcher testId="login-lang-switch" className="text-slate-500" />
          </div>
          <div className="mb-10">
            <p className="label-caps text-[#f59e0b] mb-3">{t("login_console_eyebrow")}</p>
            <h2 className="font-display text-4xl text-[#0a0f1e]">{t("login_title")}</h2>
            <p className="text-slate-500 mt-2 text-sm">{t("login_subtitle")}</p>
          </div>

          <div className="space-y-5">
            <div>
              <Label className="label-caps mb-2 block" htmlFor="email">Email</Label>
              <Input
                id="email"
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@ditours.pt"
                className="h-12 bg-transparent border-slate-200 rounded-none focus:border-[#f59e0b] text-[#0a0f1e]"
                required
              />
            </div>
            <div>
              <Label className="label-caps mb-2 block" htmlFor="password">{t("login_field_password")}</Label>
              <div className="relative">
                <Input
                  id="password"
                  data-testid="login-password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 bg-transparent border-slate-200 rounded-none focus:border-[#f59e0b] text-[#0a0f1e] pr-11"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  data-testid="login-password-toggle"
                  aria-label={showPassword ? "Ocultar palavra-passe" : "Mostrar palavra-passe"}
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 flex items-center justify-center text-slate-500 hover:text-[#f59e0b] transition-colors"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <div className="mt-2 text-right">
                <Link
                  to="/esqueci-password"
                  data-testid="login-forgot-password-link"
                  className="text-xs text-slate-500 hover:text-[#f59e0b] uppercase tracking-widest font-semibold"
                >
                  Esqueceu palavra-passe?
                </Link>
              </div>
            </div>

            {err && (
              <div data-testid="login-error" className="text-sm text-red-400 border-l-2 border-red-500 pl-3">
                {err}
              </div>
            )}

            <Button
              type="submit"
              data-testid="login-submit"
              disabled={loading}
              className="w-full h-12 rounded-none bg-[#f59e0b] hover:bg-[#d97706] text-[#0a0f1e] font-semibold tracking-wide"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <span className="flex items-center justify-center gap-2">
                  {t("login_submit")} <ChevronRight className="w-4 h-4" />
                </span>
              )}
            </Button>
          </div>

          <div className="mt-6 text-center text-xs text-slate-500 space-y-2">
            <div>
              {t("login_no_account")}{" "}
              <a href="/register" className="text-[#f59e0b] font-bold uppercase tracking-widest" data-testid="login-to-register">{t("login_create_account")}</a>
            </div>
            <div className="text-slate-500">
              Parceiro / Frota / Motorista?{" "}
              <a href="/signup" className="text-[#f59e0b] font-bold uppercase tracking-widest" data-testid="login-to-signup">Inscrever</a>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
